/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { usePrerender } from '../contexts/PrerenderContext';
import { cachedFetch, readCache } from '../lib/apiCache';
import { sameJson } from '../lib/sameJson';
import { cn } from '../lib/cn';
import { scrollElementIntoView } from '../lib/pageScroll';
import { ReadingProgressBar } from '../components/ReadingProgressBar';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Clock,
  List,
  Linkedin,
  Globe2,
  Tag,
  Twitter,
} from 'lucide-react';
import type { ArticleContentBlock } from './admin/articleTypes';
import { blockHeadingId, getSectionHeading } from './admin/articleTypes';
import { SEO_MANAGED_ATTR, buildBreadcrumbJsonLd } from '../hooks/useSEO';
import { articleMetaDescription } from '../lib/articleSeo.js';
import { brandTitle } from '../lib/templateVars.js';
import { buildArticleFAQSchema } from '../lib/articleFAQSchema';
import { removeJsonLd, setJsonLd, upsertJsonLd } from '../lib/jsonLdDom';
import { estimateReadingMinutes, readingTimeISO } from '../lib/readingTime';
import { effectiveArticleUpdatedAt } from '../lib/articleDates';
import { selectRelatedArticles } from '../lib/relatedArticles';
import { formatDateShort } from '../lib/formatters';
import { focusToObjectPosition } from '../lib/imageFocus';
import { authorPath } from '../lib/authorSlug.js';
import { IMAGE_SIZES, responsiveImageProps } from '../lib/imageVariants.js';
import {
  SHARE_IMAGE_HEIGHT,
  SHARE_IMAGE_TYPE,
  SHARE_IMAGE_WIDTH,
  shareImagePathOf,
} from '../lib/shareImage.js';
import {
  articleCategoryLabelKey,
  articleCategoryOf,
  kennisbankListingHref,
} from '../lib/kennisbankIndex.js';
import { Breadcrumb } from '../components/Breadcrumb';
import { ContentBlock } from '../components/ContentBlockRenderer';
import { ArticleShareRow, useArticleShare } from '../components/ArticleShareRow';
import { ArticleFloatingActions } from '../components/ArticleFloatingActions';
import { ArticleHelpfulWidget } from '../components/ArticleHelpfulWidget';
import { AuthorAvatar } from '../components/AuthorAvatar';
import type { PublicAuthor } from '../components/AuthorAvatar';

// Re-export so existing imports (e.g. ArticlePreviewModal) keep working without
// churning every callsite.
export { ContentBlock } from '../components/ContentBlockRenderer';

interface PublicArticle {
  id: string;
  // Effective slug for the current language (drives canonical + breadcrumb).
  slug: string;
  // Both languages' slugs, so hreflang alternates point at the right localized
  // URL. Optional for resilience against older cached API payloads.
  slugByLang?: { nl: string; en: string };
  // Does this article have a REAL English translation (src/lib/articleEnglish.js)?
  // `false` means the /en/ URL resolves but serves the Dutch fallback, so it may
  // not be advertised as an English page. Optional on purpose: a payload cached
  // before the server started sending the flag arrives as `undefined`, which is
  // treated as "assume translated" — the pre-flag behaviour.
  hasEnglish?: boolean;
  heroImage?: string;
  // Where the hero is cropped ("<x>% <y>%"); absent = centre.
  heroImageFocus?: string;
  title: string;
  excerpt: string;
  showToc?: boolean;
  blocks: ArticleContentBlock[];
  createdAt: string;
  updatedAt: string;
  // Lifetime first-party share count (optional: stale caches / older seeds).
  shareCount?: number;
  author?: PublicAuthor | null;
  // One of ARTICLE_CATEGORIES (src/lib/kennisbankIndex.js) or null. Optional:
  // payloads cached or baked before the field existed simply show no pill.
  category?: string | null;
}

export interface TocEntry {
  /** Stable React key (the block id). */
  key: string;
  /** Anchor id of the rendered `<h2>` — matches `blockHeadingId` in the body. */
  id: string;
  heading: string;
}

/** Headings that become table-of-contents entries (and `<h2>` anchors).
 * Only *section* headings qualify — a callout's title lives inside the
 * coloured box with no anchor, so listing it here would link to nothing. */
export const getTocEntries = (blocks: ArticleContentBlock[]): TocEntry[] =>
  blocks
    .map((b) => ({ block: b, heading: getSectionHeading(b) }))
    .filter((e): e is { block: ArticleContentBlock; heading: string } =>
      Boolean(e.heading && e.heading.trim())
    )
    .map((e) => ({ key: e.block.id, id: blockHeadingId(e.block), heading: e.heading }));

// Smooth scroll to the heading. The root's `scroll-padding-top` (index.css)
// keeps it clear of the sticky header — here and on direct anchor visits and
// back/forward nav alike — and the scroll is marked as the site's own, so the
// header holds still while it runs.
const scrollToHeading = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  scrollElementIntoView(el, { behavior: 'smooth', block: 'start' });
  if (typeof window !== 'undefined' && window.history?.replaceState) {
    window.history.replaceState(null, '', `#${id}`);
  }
};

/**
 * Scroll-spy: returns the id of the heading currently nearest the top of the
 * viewport (just below the sticky header). rAF-throttled passive listener,
 * SSR-guarded, no work when there are no headings.
 */
const useActiveHeading = (ids: string[]): string => {
  const [activeId, setActiveId] = useState('');
  const key = ids.join('|');
  useEffect(() => {
    if (typeof window === 'undefined' || ids.length === 0) return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const offset = 100; // header (80px) + a little breathing room
      let current = '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= offset) current = id;
        else break;
      }
      if (!current && ids[0]) current = ids[0];
      setActiveId((prev) => (prev === current ? prev : current));
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
    // ids is captured by `key`; re-run only when the set of headings changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return activeId;
};

export const TableOfContents: React.FC<{
  blocks: ArticleContentBlock[];
  label: string;
  /** `card` = boxed list (inline / mobile); `sidebar` = sticky rail with active marker. */
  variant?: 'card' | 'sidebar';
  /** Id of the heading to mark active (sidebar variant). */
  activeId?: string;
}> = ({ blocks, label, variant = 'card', activeId }) => {
  const entries = getTocEntries(blocks);
  if (entries.length < 2) return null;

  if (variant === 'sidebar') {
    return (
      <nav aria-label={label}>
        <div className="flex items-center gap-2 mb-4 text-emerald-400 [html.light_&]:text-emerald-700">
          <List className="w-4 h-4" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em]">{label}</span>
        </div>
        <ol className="space-y-1 border-l border-slate-800 [html.light_&]:border-slate-700">
          {entries.map(({ key, id, heading }) => {
            const isActive = id === activeId;
            return (
              <li key={key}>
                <a
                  href={`#${id}`}
                  onClick={(e) => scrollToHeading(e, id)}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    '-ml-px block border-l-2 pl-4 py-1.5 text-sm font-medium leading-snug transition-colors',
                    isActive
                      ? 'border-emerald-500 text-emerald-400 [html.light_&]:text-emerald-700'
                      : 'border-transparent text-slate-400 hover:text-emerald-400 [html.light_&]:text-slate-500 [html.light_&]:hover:text-emerald-700'
                  )}
                >
                  {heading}
                </a>
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  return (
    <nav
      aria-label={label}
      className="mb-12 bg-slate-900/40 border border-slate-800 rounded-3xl p-6 md:p-8 [html.light_&]:bg-slate-800 [html.light_&]:border-slate-700"
    >
      <div className="flex items-center gap-2 mb-5 text-emerald-400 [html.light_&]:text-emerald-700">
        <List className="w-4 h-4" />
        <span className="text-[10px] font-black uppercase tracking-[0.3em]">{label}</span>
      </div>
      <ol className="space-y-2 list-decimal list-inside marker:text-slate-400 marker:font-black">
        {entries.map(({ key, id, heading }) => (
          <li key={key} className="text-slate-300 font-medium leading-relaxed">
            <a
              href={`#${id}`}
              onClick={(e) => scrollToHeading(e, id)}
              className="text-slate-300 hover:text-emerald-400 transition-colors underline-offset-4 hover:underline decoration-emerald-500/40 [html.light_&]:hover:text-emerald-700"
            >
              {heading}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export const AuthorCard: React.FC<{
  author: PublicAuthor;
  language: 'nl' | 'en';
  aboutLabel: string;
}> = ({ author, language, aboutLabel }) => {
  const bio = author.bio?.[language] || author.bio?.nl || '';
  const hasLinks = author.linkedinUrl || author.twitterUrl || author.websiteUrl;
  if (!bio && !hasLinks) return null;
  return (
    <aside className="mt-16 pt-12 border-t border-slate-800">
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-6">
        <span className="text-emerald-300 text-xs font-black uppercase tracking-widest">
          {aboutLabel}
        </span>
      </div>
      <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 md:p-8 flex gap-6 items-start flex-wrap">
        <AuthorAvatar author={author} sizeClass="w-20 h-20" textClass="text-lg" />
        <div className="flex-1 min-w-[220px] space-y-3">
          <div>
            <div className="text-lg font-black text-white">
              <Link
                to={authorPath(language, author.slug)}
                className="hover:text-emerald-400 transition-colors underline-offset-4 hover:underline"
              >
                {author.displayName}
              </Link>
            </div>
            {author.title && <div className="text-xs text-slate-400 font-bold">{author.title}</div>}
          </div>
          {bio && <p className="text-slate-300 text-sm font-medium leading-relaxed">{bio}</p>}
          {hasLinks && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              {author.linkedinUrl && (
                <a
                  href={author.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 text-slate-200 text-[11px] font-black"
                >
                  <Linkedin className="w-3 h-3" /> LinkedIn
                </a>
              )}
              {author.twitterUrl && (
                <a
                  href={author.twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 text-slate-200 text-[11px] font-black"
                >
                  <Twitter className="w-3 h-3" /> X
                </a>
              )}
              {author.websiteUrl && (
                <a
                  href={author.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 text-slate-200 text-[11px] font-black"
                >
                  <Globe2 className="w-3 h-3" /> Website
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

interface ArticleIndexItem {
  id: string;
  slug: string;
  heroImage?: string;
  heroImageFocus?: string;
  title: string;
  excerpt: string;
  createdAt?: string;
  updatedAt?: string;
  readingMinutes?: number;
}

export const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { language, t, seoOverrides } = useLanguage();
  const prerender = usePrerender();

  // Seed from prerender data if it matches the current slug + language so the
  // initial render shows the article body without a loading flash. Falls back
  // to a cached fetch result for SPA navigation between articles within the
  // same browsing session.
  const seededArticle: PublicArticle | null =
    prerender?.articleDetail && prerender.lang === language && prerender.articleDetail.slug === slug
      ? prerender.articleDetail
      : (readCache<PublicArticle>(`/api/articles/${slug}?lang=${language}`) ?? null);

  const contentRef = useRef<HTMLElement>(null);
  const [article, setArticle] = useState<PublicArticle | null>(seededArticle);
  // Full published index, newest first. Prerender seeds it on article routes so
  // the related-articles block (and prev/next nav) are baked into the static
  // HTML — the internal links must be visible to non-JS crawlers. Related and
  // prev/next are DERIVED from this via deterministic pure functions; seeding
  // and deriving with the same inputs keeps SSR and hydration identical.
  const [articlesIndex, setArticlesIndex] = useState<ArticleIndexItem[]>(() => {
    if (prerender?.articlesIndex && prerender.lang === language) return prerender.articlesIndex;
    return readCache<ArticleIndexItem[]>(`/api/articles?lang=${language}`) ?? [];
  });
  // Only show the spinner when we have *nothing* to render — never flip back
  // to loading=true if we already have an article visible. Stale-while-
  // revalidate: the refetch below runs in the background.
  const [loading, setLoading] = useState(!seededArticle);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    let cancelled = false;
    cachedFetch<PublicArticle>(`/api/articles/${slug}?lang=${language}`)
      .then((data) => {
        if (cancelled) return;
        // Usually the seeded article: equal JSON keeps that object, so the page
        // does not re-render right after hydration; a real change (or another
        // article) lands as a transition, the loading flag with it.
        startTransition(() => {
          setArticle((prev) => (sameJson(prev, data) ? prev : data));
          setLoading(false);
        });
        // A former slug (renamed article, or the Dutch slug under /en once a
        // slugEn exists) resolves server-side; the URL follows the canonical
        // one so shares and bookmarks converge. The client-side arm of the
        // server's 301 for the same paths (server/spaRenderer.ts).
        const canonical = data.slugByLang?.[language];
        if (canonical && canonical !== slug) {
          navigate(`/${language}/kennisbank/${canonical}`, { replace: true });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Only surface "not found" when we have no article at all to show.
        // If a previous fetch left a usable article in state, keep showing it.
        setNotFound((prev) => prev || !seededArticle);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // seededArticle intentionally excluded — captured at mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, language]);

  useEffect(() => {
    let cancelled = false;
    cachedFetch<ArticleIndexItem[]>(`/api/articles?lang=${language}`)
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data)) {
          startTransition(() => setArticlesIndex((prev) => (sameJson(prev, data) ? prev : data)));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug, language]);

  const related = useMemo(
    () => (article ? selectRelatedArticles(article, articlesIndex) : []),
    [article, articlesIndex]
  );

  // Prev/next in publication order. The index is newest first
  // (src/lib/kennisbankIndex.js), so the OLDER neighbour sits at i + 1 and is
  // "Vorig artikel", the newer one at i - 1 is "Volgend artikel". Matched by
  // id — stable across languages. Ends yield null (no wrap-around).
  const { prevArticle, nextArticle } = useMemo(() => {
    const i = article ? articlesIndex.findIndex((a) => a.id === article.id) : -1;
    if (i === -1) return { prevArticle: null, nextArticle: null };
    return {
      prevArticle: i < articlesIndex.length - 1 ? articlesIndex[i + 1] : null,
      nextArticle: i > 0 ? articlesIndex[i - 1] : null,
    };
  }, [article, articlesIndex]);

  // One share-state instance feeds both the docked bottom row and the
  // floating scroll pill — events, counter pings and the optimistic count
  // stay in lockstep between them.
  const share = useArticleShare(
    article
      ? {
          articleId: article.id,
          slug: article.slug,
          url: `https://ere-vergelijk.nl/${language}/kennisbank/${article.slug}`,
          title: article.title,
          initialShareCount: article.shareCount,
        }
      : null
  );
  // Anchor for the floating pill's hide condition (the docked bottom bar).
  const bottomBarRef = useRef<HTMLDivElement>(null);

  // Dynamic SEO — title, description, canonical, hreflang, JSON-LD (Article + Breadcrumb)
  useEffect(() => {
    if (!article) return;
    const HOST = 'https://ere-vergelijk.nl';

    // SEO Studio overrides keyed by article ID. Empty string = "no override",
    // fall back to the article body. Booleans / non-strings (noindex) handled
    // separately below.
    const ovTitleRaw = seoOverrides[`article_${article.id}_title`];
    const ovDescRaw = seoOverrides[`article_${article.id}_description`];
    const ovOgRaw = seoOverrides[`article_${article.id}_og_image`];
    const ovNoindexRaw = seoOverrides[`article_${article.id}_noindex`];
    const ovCustomRaw = seoOverrides[`article_${article.id}_custom_schema`];
    const ovTitle = typeof ovTitleRaw === 'string' ? ovTitleRaw.trim() : '';
    const ovDesc = typeof ovDescRaw === 'string' ? ovDescRaw.trim() : '';
    const ovOg = typeof ovOgRaw === 'string' ? ovOgRaw.trim() : '';
    const noindex = ovNoindexRaw === true || ovNoindexRaw === 'true';

    const effectiveTitle = ovTitle || article.title;
    // One chain for both head mirrors and this page (src/lib/articleSeo.js):
    // the override as typed, else the excerpt or the first block, capped.
    const effectiveDesc = articleMetaDescription({
      override: ovDesc,
      excerpt: article.excerpt,
      blocks: article.blocks,
      fallback: t('seo.faq_description'),
    });
    const effectiveHero = ovOg || article.heroImage || '';

    // ── English-stub gate ──────────────────────────────────────────────────
    // Mirrors the branch in server/seoMeta.ts (and its twin in
    // scripts/prerender.mjs) that bakes the same decision into the static HTML.
    // An article with no real English translation still SERVES its /en/ URL —
    // the Dutch fallback, so an inbound link is never a 404 — but the crawler
    // signals must not claim an English page exists.
    //   · on the /en/ URL of such an article: noindex,follow and NO hreflang
    //   · on its /nl/ URL: self + x-default, but never an `en` alternate
    // `hasEnglish === undefined` (older cached payload) keeps the pre-flag
    // behaviour: assume the translation exists rather than noindex a page that
    // may well be translated.
    const enStubArticle = article.hasEnglish === false;
    const enStubUrl = enStubArticle && language === 'en';
    // The language the BODY is actually in. A stub /en/ URL renders Dutch, so
    // its JSON-LD must say nl-NL — `en-US` would describe a page that does not
    // exist, and contradict the noindex the robots branch below puts on it.
    const contentLanguage: 'nl' | 'en' = enStubUrl ? 'nl' : language;

    // Branded once, by the head's own rule: an override that already names the
    // site keeps its own form.
    const brandedTitle = brandTitle(effectiveTitle);
    document.title = brandedTitle;
    document.documentElement.lang = language;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const setOG = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const setLink = (rel: string, attrs: Record<string, string>) => {
      const selector = Object.entries(attrs)
        .filter(([k]) => k !== 'href')
        .map(([k, v]) => `[${k}="${v}"]`)
        .join('');
      let el = document.querySelector(`link[rel="${rel}"]${selector}`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
        return;
      }
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    };
    const removeLink = (rel: string, attrs: Record<string, string>) => {
      const selector = Object.entries(attrs)
        .map(([k, v]) => `[${k}="${v}"]`)
        .join('');
      document.querySelectorAll(`link[rel="${rel}"]${selector}`).forEach((el) => el.remove());
    };
    // Robots is the one head tag this page shares with tags it did not write:
    // the prerender bakes `noindex, follow` on an English stub, and the whole
    // acceptance environment ships a site-wide `noindex, nofollow`. The
    // unscoped `querySelector('meta[name="robots"]').remove()` that used to
    // live here deleted both — un-noindexing acceptance and reversing the stub
    // gate on every article page. Everything below touches only the meta this
    // effect created, tagged with the shared SEO_MANAGED_ATTR marker.
    const MANAGED_ROBOTS = `meta[name="robots"][${SEO_MANAGED_ATTR}="1"]`;
    const setManagedRobots = (content: string) => {
      let el = document.querySelector(MANAGED_ROBOTS);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', 'robots');
        el.setAttribute(SEO_MANAGED_ATTR, '1');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const removeManagedRobots = () => document.querySelector(MANAGED_ROBOTS)?.remove();

    setMeta('description', effectiveDesc);
    const canonicalPath = `/${language}/kennisbank/${article.slug}`;
    const canonicalUrl = `${HOST}${canonicalPath}`;
    const articleImage = effectiveHero
      ? effectiveHero.startsWith('http')
        ? effectiveHero
        : `${HOST}${effectiveHero.startsWith('/') ? '' : '/'}${effectiveHero}`
      : `${HOST}/og-image.jpg?v=10`;
    // The share tags carry the hero's share crop (src/lib/shareImage.js), a
    // 1200×630 JPEG cut at the hero's own position, as the baked head does
    // (ogImageFocusOf in src/lib/pageSeoOverrides.js: the SEO tab's image has
    // no position); the Article JSON-LD keeps the hero itself.
    const shareFocus = ovOg ? '' : article.heroImageFocus;
    const shareCrop = effectiveHero ? shareImagePathOf(effectiveHero, HOST, shareFocus) : null;
    const shareImage = shareCrop ? `${HOST}${shareCrop}` : articleImage;

    setOG('og:title', brandedTitle);
    setOG('og:description', effectiveDesc);
    setOG('og:type', 'article');
    setOG('og:url', canonicalUrl);
    setOG('og:image', shareImage);
    setOG('og:image:alt', effectiveTitle);
    if (shareCrop || !effectiveHero) {
      setOG('og:image:type', SHARE_IMAGE_TYPE);
      setOG('og:image:width', String(SHARE_IMAGE_WIDTH));
      setOG('og:image:height', String(SHARE_IMAGE_HEIGHT));
    } else {
      // An image of another host: its size is unknown, so no stale 1200×630.
      document
        .querySelectorAll(
          'meta[property="og:image:type"], meta[property="og:image:width"], meta[property="og:image:height"]'
        )
        .forEach((el) => el.remove());
    }
    // og:locale describes the words served — Dutch on a stub /en/ URL — and an
    // article in one language has no alternate locale (the bake's rule,
    // applyBaselineHead in src/lib/pageSeoOverrides.js).
    setOG('og:locale', contentLanguage === 'nl' ? 'nl_NL' : 'en_US');
    if (enStubArticle) {
      document
        .querySelectorAll('meta[property="og:locale:alternate"]')
        .forEach((el) => el.remove());
    } else {
      setOG('og:locale:alternate', contentLanguage === 'nl' ? 'en_US' : 'nl_NL');
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', brandedTitle);
    setMeta('twitter:description', effectiveDesc);
    setMeta('twitter:image', shareImage);

    // Robots. The editor's explicit SEO-Studio opt-out wins — and it also
    // withholds link equity, which the stub gate deliberately does not. Created
    // on demand and torn down on cleanup so navigating between articles doesn't
    // leak the directive.
    if (noindex) {
      setManagedRobots('noindex,nofollow');
    } else if (enStubUrl) {
      setManagedRobots('noindex, follow');
    } else {
      removeManagedRobots();
    }

    setLink('canonical', { href: canonicalUrl });
    // hreflang alternates use each language's own slug (NL and EN URLs can
    // differ). Falls back to the current effective slug if slugByLang is absent.
    // The removes are not dead code: they undo the alternates baked for a
    // previous article during SPA navigation, so a stub never inherits a
    // neighbour's cluster.
    const altHref = (l: 'nl' | 'en') =>
      `${HOST}/${l}/kennisbank/${article.slugByLang?.[l] ?? article.slug}`;
    if (enStubUrl) {
      // Not an English page: annotate nothing, in either direction.
      removeLink('alternate', { hreflang: 'nl' });
      removeLink('alternate', { hreflang: 'en' });
      removeLink('alternate', { hreflang: 'x-default' });
    } else {
      setLink('alternate', { hreflang: 'nl', href: altHref('nl') });
      if (enStubArticle) removeLink('alternate', { hreflang: 'en' });
      else setLink('alternate', { hreflang: 'en', href: altHref('en') });
      setLink('alternate', { hreflang: 'x-default', href: altHref('nl') });
    }

    const personAuthor = article.author
      ? (() => {
          const sameAs = [
            article.author.linkedinUrl,
            article.author.twitterUrl,
            article.author.websiteUrl,
          ].filter((u): u is string => Boolean(u));
          const authorImage = article.author.avatarPath
            ? article.author.avatarPath.startsWith('http')
              ? article.author.avatarPath
              : `${HOST}${article.author.avatarPath}`
            : undefined;
          return {
            '@type': 'Person',
            name: article.author.displayName,
            ...(article.author.title ? { jobTitle: article.author.title } : {}),
            ...(authorImage ? { image: authorImage } : {}),
            ...(sameAs.length > 0 ? { sameAs } : {}),
            url: `${HOST}/${language}/kennisbank/${article.slug}`,
          };
        })()
      : { '@type': 'Organization', name: 'ERE-Vergelijk', url: HOST };

    setJsonLd('jsonld-article', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: effectiveTitle,
      description: effectiveDesc,
      image: articleImage,
      // The language of the CONTENT, not of the URL — see contentLanguage above.
      inLanguage: contentLanguage === 'nl' ? 'nl-NL' : 'en-US',
      datePublished: article.createdAt,
      dateModified: effectiveArticleUpdatedAt(article),
      timeRequired: readingTimeISO(estimateReadingMinutes(article.blocks, language)),
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      author: personAuthor,
      publisher: {
        '@type': 'Organization',
        name: 'ERE-Vergelijk',
        logo: { '@type': 'ImageObject', url: `${HOST}/logo.png` },
      },
    });

    setJsonLd(
      'jsonld-breadcrumb',
      buildBreadcrumbJsonLd(
        [
          { label: t('nav.faq'), path: `/${language}/kennisbank` },
          { label: effectiveTitle, path: canonicalPath },
        ],
        language
      )
    );

    // FAQPage JSON-LD aggregated from every `kind: 'faq'` content block in the
    // article (with per-block / per-item opt-outs). Removed when no qualifying
    // question survives so we never emit an empty FAQPage.
    // Same rule as the Article node: `language` here only picks `inLanguage`,
    // and the questions come from the Dutch fallback blocks on a stub.
    upsertJsonLd('jsonld-faq', buildArticleFAQSchema(article.blocks, contentLanguage));

    // Custom JSON-LD from SEO Studio. Validate by parsing; only inject if it
    // round-trips cleanly. Stored in its own <script> so the auto schemas
    // remain untouched.
    if (typeof ovCustomRaw === 'string' && ovCustomRaw.trim()) {
      try {
        const parsed = JSON.parse(ovCustomRaw);
        setJsonLd('jsonld-article-custom', parsed);
      } catch {
        removeJsonLd('jsonld-article-custom');
      }
    } else {
      removeJsonLd('jsonld-article-custom');
    }

    return () => {
      removeJsonLd('jsonld-article');
      removeJsonLd('jsonld-breadcrumb');
      removeJsonLd('jsonld-faq');
      removeJsonLd('jsonld-article-custom');
      // Only ours — see setManagedRobots above. Unscoped, this line removed the
      // acceptance environment's site-wide noindex on the way out of an article.
      removeManagedRobots();
    };
  }, [article, language, seoOverrides, t]);

  // Table-of-contents entries (also the `<h2>` anchor ids). Drives both the
  // sticky sidebar rail and the inline mobile card, and the scroll-spy below.
  const tocEntries = useMemo(() => (article ? getTocEntries(article.blocks) : []), [article]);
  const hasToc = Boolean(article?.showToc) && tocEntries.length >= 2;
  const activeId = useActiveHeading(hasToc ? tocEntries.map((e) => e.id) : []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="bg-transparent min-h-screen">
        <section className="relative py-24 overflow-hidden bg-slate-900">
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-600 rounded-full blur-[120px]" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600 rounded-full blur-[120px]" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h1 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tight">404</h1>
            <p className="text-slate-400 text-xl max-w-2xl mx-auto font-medium leading-relaxed mb-10">
              {language === 'nl'
                ? 'Dit artikel bestaat niet of is niet gepubliceerd.'
                : 'This article does not exist or is not published.'}
            </p>
            <Link
              to={`/${language}/kennisbank`}
              className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 font-bold transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('kennisbank.back_to_overview')}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const category = articleCategoryOf(article);

  return (
    <div className="bg-transparent min-h-screen">
      {/* Reading-progress line — sticks just under the header, fills as you scroll
          the body. trackSlug arms the 25/50/75/100% article_read_progress events. */}
      <ReadingProgressBar targetRef={contentRef} trackSlug={article.slug} />

      {/* Hero — matches other page heroes */}
      <section className="relative py-24 overflow-hidden bg-slate-900">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-600 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600 rounded-full blur-[120px]" />
          {article.heroImage && (
            <img
              {...responsiveImageProps(article.heroImage, IMAGE_SIZES.backdrop)}
              alt=""
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-40 mix-blend-soft-light [html.light_&]:opacity-70"
              style={
                article.heroImageFocus
                  ? { objectPosition: focusToObjectPosition(article.heroImageFocus) }
                  : undefined
              }
            />
          )}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center reveal-rise">
            {/* Back link as badge, plus the article's category — which opens
                the listing filtered on it. */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
              <Link
                to={`/${language}/kennisbank`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 text-emerald-400 text-xs font-black uppercase tracking-widest hover:bg-emerald-500/20 transition-all"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t('kennisbank.back_to_overview')}
              </Link>
              {category && (
                <Link
                  to={kennisbankListingHref(language, { category })}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900/60 rounded-full border border-slate-700 text-slate-300 text-xs font-black uppercase tracking-widest hover:border-emerald-500/40 hover:text-white transition-all"
                >
                  <Tag aria-hidden="true" className="w-3.5 h-3.5 text-emerald-400" />
                  {t(articleCategoryLabelKey(category))}
                </Link>
              )}
            </div>

            <h1 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tight leading-tight">
              {article.title}
            </h1>

            {article.excerpt && (
              <p className="text-slate-400 text-xl max-w-3xl mx-auto font-medium leading-relaxed">
                {article.excerpt}
              </p>
            )}

            {(() => {
              const publishedDate = article.createdAt
                ? formatDateShort(article.createdAt, language)
                : '';
              const effectiveUpdated = effectiveArticleUpdatedAt(article);
              const updatedDate = formatDateShort(effectiveUpdated, language);
              // Only surface "Bijgewerkt" when it lands on a different calendar
              // day than the publish date — a same-day touch adds no signal.
              const showUpdated = updatedDate !== publishedDate;
              return (
                <div className="mt-10 flex justify-center items-center gap-x-4 gap-y-3 flex-wrap text-slate-400 text-sm">
                  {article.author && (
                    <div className="flex items-center gap-3">
                      <AuthorAvatar
                        author={article.author}
                        sizeClass="w-11 h-11"
                        textClass="text-sm"
                      />
                      <div className="text-left">
                        <span>{t('kennisbank.by_author')} </span>
                        <span className="font-black text-white">{article.author.displayName}</span>
                        {article.author.title && <span>, {article.author.title}</span>}
                      </div>
                    </div>
                  )}
                  {article.author && (
                    <span aria-hidden="true" className="text-slate-600">
                      ·
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-emerald-400" />
                    {estimateReadingMinutes(article.blocks, language)}{' '}
                    {t('kennisbank.reading_time_minutes')}
                  </span>
                  {publishedDate && (
                    <>
                      <span aria-hidden="true" className="text-slate-600">
                        ·
                      </span>
                      <span>
                        {showUpdated && `${t('kennisbank.published_on')} `}
                        <time dateTime={article.createdAt}>{publishedDate}</time>
                      </span>
                    </>
                  )}
                  {showUpdated && (
                    <>
                      <span aria-hidden="true" className="text-slate-600">
                        ·
                      </span>
                      <span>
                        {t('kennisbank.updated_on')}{' '}
                        <time dateTime={effectiveUpdated}>{updatedDate}</time>
                      </span>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      <Breadcrumb
        trail={[
          { label: t('nav.faq'), path: `/${language}/kennisbank` },
          { label: article.title, path: `/${language}/kennisbank/${article.slug}` },
        ]}
      />

      {/* Content */}
      <section ref={contentRef} className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={cn(
              hasToc &&
                'lg:grid lg:grid-cols-[16rem_minmax(0,48rem)] lg:items-start lg:justify-center lg:gap-12'
            )}
          >
            {/* Sticky table of contents — pinned to the left on lg+, body scrolls past it */}
            {hasToc && (
              <aside className="hidden self-start lg:sticky lg:top-24 lg:block max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
                <TableOfContents
                  blocks={article.blocks}
                  label={t('kennisbank.toc_title')}
                  variant="sidebar"
                  activeId={activeId}
                />
              </aside>
            )}

            {/* Article body in a centered reading column */}
            <div
              className="max-w-3xl mx-auto w-full min-w-0 reveal-rise"
              style={{ animationDelay: '100ms' }}
            >
              {/* Inline TOC for mobile / tablet — the sticky sidebar replaces it on lg+ */}
              {hasToc && (
                <div className="lg:hidden">
                  <TableOfContents blocks={article.blocks} label={t('kennisbank.toc_title')} />
                </div>
              )}
              {article.blocks.map((block) => (
                <ContentBlock key={block.id} block={block} />
              ))}

              <ArticleHelpfulWidget articleId={article.id} articleSlug={article.slug} />

              {article.author && (
                <AuthorCard
                  author={article.author}
                  language={language}
                  aboutLabel={t('kennisbank.about_the_author')}
                />
              )}

              {/* Related articles */}
              {related.length > 0 && (
                <div className="mt-16 pt-12 border-t border-slate-800">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full border border-amber-500/20 mb-8">
                    <BookOpen className="w-4 h-4 text-amber-300" />
                    <span className="text-amber-300 text-xs font-black uppercase tracking-widest">
                      {t('kennisbank.related_articles')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {related.map((rel) => (
                      <div className="reveal-rise" key={rel.id}>
                        <Link
                          to={`/${language}/kennisbank/${rel.slug}`}
                          className="group block bg-slate-800/30 border border-slate-700/50 rounded-3xl overflow-hidden hover:border-emerald-500/30 transition-all h-full"
                        >
                          {rel.heroImage && (
                            <div className="h-36 overflow-hidden">
                              <img
                                {...responsiveImageProps(rel.heroImage, IMAGE_SIZES.card)}
                                alt={rel.title}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                style={
                                  rel.heroImageFocus
                                    ? { objectPosition: focusToObjectPosition(rel.heroImageFocus) }
                                    : undefined
                                }
                              />
                            </div>
                          )}
                          <div className="p-6">
                            <h3 className="text-base font-black text-white mb-2 group-hover:text-emerald-400 transition-colors leading-snug">
                              {rel.title}
                            </h3>
                            <p className="text-slate-400 text-sm font-medium leading-relaxed line-clamp-2 mb-4">
                              {rel.excerpt}
                            </p>
                            <div className="flex items-center gap-2 text-emerald-500 font-black text-xs">
                              {t('kennisbank.read_more')}
                              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prev/next in publication order (newest first, see prevArticle above) */}
              {(prevArticle || nextArticle) && (
                <nav
                  aria-label={t('kennisbank.article_nav_label')}
                  className="mt-16 pt-8 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  {prevArticle && (
                    <Link
                      to={`/${language}/kennisbank/${prevArticle.slug}`}
                      rel="prev"
                      className="group block bg-slate-800/30 border border-slate-700/50 rounded-3xl p-6 hover:border-emerald-500/30 transition-all"
                    >
                      <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-fg-muted mb-2">
                        <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                        {t('kennisbank.previous_article')}
                      </span>
                      <span className="block text-base font-black text-white group-hover:text-emerald-400 transition-colors leading-snug line-clamp-2">
                        {prevArticle.title}
                      </span>
                    </Link>
                  )}
                  {nextArticle && (
                    <Link
                      to={`/${language}/kennisbank/${nextArticle.slug}`}
                      rel="next"
                      className="group block bg-slate-800/30 border border-slate-700/50 rounded-3xl p-6 hover:border-emerald-500/30 transition-all text-right sm:col-start-2"
                    >
                      <span className="flex items-center justify-end gap-2 text-[11px] font-black uppercase tracking-widest text-fg-muted mb-2">
                        {t('kennisbank.next_article')}
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </span>
                      <span className="block text-base font-black text-white group-hover:text-emerald-400 transition-colors leading-snug line-clamp-2">
                        {nextArticle.title}
                      </span>
                    </Link>
                  )}
                </nav>
              )}

              {/* Bottom bar: back link + share row. Also the floating pill's
                  hide anchor — once this scrolls into view the pill yields. */}
              <div
                ref={bottomBarRef}
                className="mt-16 pt-8 border-t border-slate-800 flex items-center gap-x-4 gap-y-3 flex-wrap"
              >
                <Link
                  to={`/${language}/kennisbank`}
                  className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 font-bold transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('kennisbank.back_to_overview')}
                </Link>
                <div className="flex-1 h-px bg-slate-800 min-w-8" />
                <ArticleShareRow share={share} />
              </div>

              <ArticleFloatingActions
                share={share}
                contentRef={contentRef}
                dockRef={bottomBarRef}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
